import { flushSync } from 'react-dom'
import type { Locale } from 'shared/types'

const appEnv = typeof window === 'undefined' ? process.env.APP_ENV : window.APP_ENV
export const isDevEnv = appEnv === 'development' || appEnv === 'local'
// A checkout, NOT the dev DEPLOYMENT (which `isDevEnv` includes) — gate anything that must never reach a
// deployed environment on this.
export const isLocalEnv = appEnv === 'local'

export { cn } from 'cn'

const numberFormatCache = new Map<string, Intl.NumberFormat>()
const dateFormatCache = new Map<string, Intl.DateTimeFormat>()

function getNumberFormatter(locale: string, decimalDigits: number): Intl.NumberFormat {
  // Normalize locale for SSR consistency
  const normalizedLocale = locale === 'eu' || locale === 'gl' || locale === 'it' ? 'es' : locale

  const cacheKey = `${normalizedLocale}-${decimalDigits}`

  let formatter = numberFormatCache.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.NumberFormat(normalizedLocale, {
      style: 'decimal',
      minimumFractionDigits: decimalDigits,
      maximumFractionDigits: decimalDigits,
    })
    numberFormatCache.set(cacheKey, formatter)
  }

  return formatter
}

export function formatNumber(locale: string, number: number, decimalDigits = 2) {
  // Fix SSR mismatch error where numbers in Euskera or Galician are formatted in chrome with commas but in node are dots
  return getNumberFormatter(locale, decimalDigits).format(number)
}

// Module-private: route-transition has its own ported `prefersReducedMotion`, and a second export would compete with it.
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

// Decided here, at the navigate() call, because the ROUTER calls startViewTransition and the UA's own 250ms root
// cross-fade keeps the ::view-transition overlay alive even where app.css zeroes the animations we declare.
// While that overlay is up a press hit-tests the captured <html> and is swallowed (measured in ranking-sortable.e2e).
export function flowViewTransition(): boolean {
  return !prefersReducedMotion()
}

// The landing and the way in are one journey, so the boundary is played rather than cut: see
// routes/_app/-components/auth/README.md for the two directions and what each one moves.
export function authViewTransition(direction: 'enter' | 'exit') {
  return prefersReducedMotion() ? false : { types: [`auth-${direction}`] }
}

// flushSync commits before the snapshot is taken.
// Skipped under reduced motion: an accessibility requirement, and it keeps the overlay from swallowing clicks under test.
export function withViewTransition(update: () => void) {
  if (!prefersReducedMotion() && typeof document !== 'undefined' && 'startViewTransition' in document) {
    document.startViewTransition(() => flushSync(update))
  } else {
    update()
  }
}

export const getIndicatorsColor = (type: string, id: number) => {
  const colourDivitions =
    type === 'comparison' ||
    type === 'competences' ||
    type === 'values' ||
    type === 'personality' ||
    type === 'directives'
      ? ([6, 12, 18] as const)
      : type === 'vectors' || type === 'principles'
        ? ([2, 4, 6] as const)
        : type === 'dimensions' || type === 'scopes'
          ? ([1, 2, 3] as const)
          : []

  return type === 'comparison'
    ? {
        normal: 'var(--color-radar-compare)',
        darker: 'var(--color-radar-compare)',
      }
    : type === 'directives'
      ? {
          normal: 'var(--color-purple)',
          darker: 'var(--color-purple)',
        }
      : type === 'positioning'
        ? {
            normal: 'var(--color-radar-hippie-blue)',
            darker: 'var(--color-radar-hippie-blue-darker)', // Hippie Blue
          }
        : type === 'task' || id < colourDivitions[0]
          ? {
              text: 'var(--color-radar-blue-text)',
              normal: 'var(--color-radar-blue)',
              darker: 'var(--color-radar-blue-darker)',
              lighter: 'var(--color-radar-blue-lightest)',
              disabled: 'var(--color-radar-blue-disabled)',
            }
          : type === 'people' || id < colourDivitions[1]
            ? {
                text: 'var(--color-radar-green-text)',
                normal: 'var(--color-radar-green)',
                darker: 'var(--color-radar-green-darker)',
                lighter: 'var(--color-radar-green-lightest)',
                disabled: 'var(--color-radar-green-disabled)',
              }
            : type === 'context' || id < colourDivitions[2]
              ? {
                  text: 'var(--color-radar-yellow-text)',
                  normal: 'var(--color-radar-yellow)',
                  darker: 'var(--color-radar-yellow-darker)',
                  lighter: 'var(--color-radar-yellow-lightest)',
                  disabled: 'var(--color-radar-yellow-disabled)',
                }
              : {
                  normal: 'var(--bg-color-2)',
                  darker: 'var(--bg-color-2)',
                }
}

export const getInitials = (name: string, type: string) => {
  if (type === 'team' || type === 'org_group' || type === 'job') {
    const nameParts = name.split(' ')
    return nameParts.length > 1
      ? nameParts
          .reduce((previous, current) => `${previous}${current.length > 2 ? current.charAt(0).toUpperCase() : ''}`, '')
          .substring(0, 2)
      : name.substring(0, 2).toUpperCase()
  }

  return name.substring(0, 1).toUpperCase()
}

export const getAgeValue = (
  values: [number, number],
  opts = { showLimits: false },
): { age: string; plural: 'one' | 'other' } =>
  (values[0] >= 0 || values[1] <= 90 || opts.showLimits) && values[0] !== values[1]
    ? { age: `${values[0]} - ${values[1]}`, plural: values[1] === 1 ? 'one' : 'other' }
    : values[0] > 0
      ? { age: values[0].toString(), plural: values[0] === 1 ? 'one' : 'other' }
      : { age: values[1].toString(), plural: values[1] === 1 ? 'one' : 'other' }

export const formatDatetime = (dateString: string | undefined) => {
  if (!dateString) return

  const date = new Date(dateString)

  const year = date.getFullYear().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export const isEmpty = (input?: unknown) => input == null || input === ''

export const basename =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/danchiano') ? '/danchiano' : ''

export const formatDateLocale = (date: string | undefined, locale: Locale) => {
  if (!date) return undefined
  let formatter = dateFormatCache.get(locale)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' })
    dateFormatCache.set(locale, formatter)
  }
  return formatter.format(new Date(date))
}

export function getSectionIconColors(theme: 'emerald' | 'amber' | 'blue' | 'violet' | 'primary' | 'rose') {
  switch (theme) {
    case 'emerald':
      return {
        text: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-500/10',
      }
    case 'amber':
      return {
        text: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-500/10',
      }
    case 'blue':
      return {
        text: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-500/10',
      }
    case 'violet':
      return {
        text: 'text-violet-600 dark:text-violet-400',
        bg: 'bg-violet-500/10',
      }
    case 'rose':
      return {
        text: 'text-rose-600 dark:text-rose-400',
        bg: 'bg-rose-500/10',
      }
    case 'primary':
      return {
        text: 'text-primary',
        bg: 'bg-primary/10',
      }
  }
}
